/**
 * Automation service — client access to rule-based automations.
 *
 * Tables: workspace_automations, automation_runs (migration 817). Rules are
 * read directly (RLS: workspace members) and written only through the
 * governed RPCs create_automation / update_automation /
 * set_automation_enabled / delete_automation (workspace admins). The engine
 * itself runs in the database and the ambient runtime; this module only
 * configures and observes it.
 *
 * Vocabulary and the words a rule is said in: automationRules.ts.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';
import type { ActionKind, AutomationDraft, PreviewAction, PreviewResult, RuleAction, TriggerKind } from './automationRules';

/** A draft as it is saved: the sentence is its name. */
export type SavedDraft = AutomationDraft & { name: string; moreActions: RuleAction[] };

export type AutomationRow = Database['public']['Tables']['workspace_automations']['Row'];
export type AutomationRunRow = Database['public']['Tables']['automation_runs']['Row'];

export interface Automation {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  triggerKind: TriggerKind;
  triggerConfig: Record<string, unknown>;
  conditions: Record<string, unknown>;
  actionKind: ActionKind;
  actionConfig: Record<string, unknown>;
  /** Actions after the first (migration 832). */
  moreActions: RuleAction[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lastFiredAt: string | null;
  fireCount: number;
}

export interface AutomationRun {
  id: string;
  automationId: string;
  triggerKind: TriggerKind;
  subjectKind: 'item' | 'cycle' | 'agent_finding';
  subjectId: string;
  subjectTitle: string | null;
  actionKind: ActionKind;
  status: string;
  detail: Record<string, unknown>;
  actedAs: string | null;
  createdAt: string;
  completedAt: string | null;
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

export function rowToAutomation(r: AutomationRow): Automation {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    name: r.name,
    description: r.description,
    enabled: r.enabled,
    triggerKind: r.trigger_kind as TriggerKind,
    triggerConfig: obj(r.trigger_config),
    conditions: obj(r.conditions),
    actionKind: r.action_kind as ActionKind,
    actionConfig: obj(r.action_config),
    moreActions: (Array.isArray(r.more_actions) ? r.more_actions : [])
      .map((a) => obj(a))
      .filter((a) => typeof a.kind === 'string')
      .map((a) => ({ kind: a.kind as ActionKind, config: obj(a.config) })),
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    lastFiredAt: r.last_fired_at,
    fireCount: r.fire_count,
  };
}

export function rowToRun(r: AutomationRunRow): AutomationRun {
  return {
    id: r.id,
    automationId: r.automation_id,
    triggerKind: r.trigger_kind as TriggerKind,
    subjectKind: r.subject_kind as AutomationRun['subjectKind'],
    subjectId: r.subject_id,
    subjectTitle: r.subject_title,
    actionKind: r.action_kind as ActionKind,
    status: r.status,
    detail: obj(r.detail),
    actedAs: r.acted_as,
    createdAt: r.created_at,
    completedAt: r.completed_at,
  };
}

type Result<T> = { data: T; error: string | null };

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** A rule's name is its sentence, so a second identical rule hits the unique name. */
function plainSaveError(raw: string): string {
  if (raw.includes('workspace_automations_name_unique')) return 'This workspace already has this exact rule.';
  if (raw.includes('preview_automation') && /Could not find the function|does not exist/.test(raw)) {
    return 'The try-it check is not available on this database yet.';
  }
  return raw.replace(/^(invalid_automation|not_authorized): /, '');
}

export async function listAutomations(workspaceId: string): Promise<Result<Automation[]>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db
      .from('workspace_automations')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(rowToAutomation), error: null };
  } catch (err) {
    return { data: [], error: message(err) };
  }
}

export async function listAutomationRuns(workspaceId: string, limit = 50): Promise<Result<AutomationRun[]>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db
      .from('automation_runs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(rowToRun), error: null };
  } catch (err) {
    return { data: [], error: message(err) };
  }
}

export async function createAutomation(workspaceId: string, draft: SavedDraft): Promise<Result<string | null>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db.rpc('create_automation', {
      p_workspace_id: workspaceId,
      p_name: draft.name,
      p_description: '',
      p_trigger_kind: draft.triggerKind,
      p_trigger_config: draft.triggerConfig as never,
      p_conditions: draft.conditions as never,
      p_action_kind: draft.actionKind,
      p_action_config: draft.actionConfig as never,
      p_more_actions: (draft.moreActions ?? []) as never,
    });
    if (error) return { data: null, error: plainSaveError(error.message) };
    return { data: data ?? null, error: null };
  } catch (err) {
    return { data: null, error: message(err) };
  }
}

export async function updateAutomation(id: string, draft: SavedDraft): Promise<{ error: string | null }> {
  try {
    const db = getTypedSupabaseClient();
    const { error } = await db.rpc('update_automation', {
      p_id: id,
      p_name: draft.name,
      p_description: '',
      p_trigger_kind: draft.triggerKind,
      p_trigger_config: draft.triggerConfig as never,
      p_conditions: draft.conditions as never,
      p_action_kind: draft.actionKind,
      p_action_config: draft.actionConfig as never,
      p_more_actions: (draft.moreActions ?? []) as never,
    });
    return { error: error ? plainSaveError(error.message) : null };
  } catch (err) {
    return { error: message(err) };
  }
}

export async function setAutomationEnabled(id: string, enabled: boolean): Promise<{ error: string | null }> {
  try {
    const db = getTypedSupabaseClient();
    const { error } = await db.rpc('set_automation_enabled', { p_id: id, p_enabled: enabled });
    return { error: error ? error.message : null };
  } catch (err) {
    return { error: message(err) };
  }
}

export async function deleteAutomation(id: string): Promise<{ error: string | null }> {
  try {
    const db = getTypedSupabaseClient();
    const { error } = await db.rpc('delete_automation', { p_id: id });
    return { error: error ? error.message : null };
  } catch (err) {
    return { error: message(err) };
  }
}

/** Groups a notify action can address. */
export async function listWorkspaceGroups(workspaceId: string): Promise<Result<Array<{ id: string; name: string }>>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db
      .from('groups')
      .select('id, name, display_name')
      .eq('workspace_id', workspaceId)
      .order('display_name', { ascending: true });
    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((g) => ({ id: g.id, name: g.display_name || g.name })),
      error: null,
    };
  } catch (err) {
    return { data: [], error: message(err) };
  }
}

/** Queues (org units with queueing on) an item can be moved to or found in. */
export async function listWorkspaceQueues(
  workspaceId: string,
): Promise<Result<Array<{ id: string; name: string; hasDefaultOwner: boolean }>>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db
      .from('org_units')
      .select('id, name, display_name, queue_settings')
      .eq('workspace_id', workspaceId)
      .order('display_name', { ascending: true });
    if (error) return { data: [], error: error.message };
    const queues = (data ?? [])
      .filter((u) => obj(u.queue_settings).enabled === true)
      .map((u) => ({
        id: u.id,
        name: u.display_name || u.name,
        hasDefaultOwner: typeof obj(u.queue_settings).defaultStewardId === 'string' && obj(u.queue_settings).defaultStewardId !== '',
      }));
    return { data: queues, error: null };
  } catch (err) {
    return { data: [], error: message(err) };
  }
}

/**
 * What a draft would do to one item, without doing it (migration 826). The
 * database runs the real engine on the item and rolls everything back.
 */
export async function previewAutomation(
  workspaceId: string,
  itemId: string,
  draft: AutomationDraft,
): Promise<Result<PreviewResult | null>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db.rpc('preview_automation', {
      p_workspace_id: workspaceId,
      p_item_id: itemId,
      p_trigger_kind: draft.triggerKind,
      p_trigger_config: draft.triggerConfig as never,
      p_conditions: draft.conditions as never,
      p_action_kind: draft.actionKind,
      p_action_config: draft.actionConfig as never,
      p_more_actions: (draft.moreActions ?? []) as never,
    });
    if (error) return { data: null, error: plainSaveError(error.message) };
    const r = obj(data);
    return {
      data: {
        matches: r.matches === true,
        unmet: Array.isArray(r.unmet) ? (r.unmet as string[]) : undefined,
        status: typeof r.status === 'string' ? r.status : undefined,
        detail: obj(r.detail),
        recipients: Array.isArray(r.recipients) ? (r.recipients as string[]) : [],
        itemTitle: typeof r.item_title === 'string' ? r.item_title : undefined,
        actions: Array.isArray(r.actions)
          ? (r.actions as unknown[]).map((x): PreviewAction => {
              const a = obj(x);
              return {
                kind: a.kind as ActionKind,
                status: typeof a.status === 'string' ? a.status : 'failed',
                detail: obj(a.detail),
                recipients: Array.isArray(a.recipients) ? (a.recipients as string[]) : [],
                ownerMissing: a.owner_missing === true,
              };
            })
          : undefined,
      },
      error: null,
    };
  } catch (err) {
    return { data: null, error: message(err) };
  }
}

/**
 * The event list of each webhook address registered in the workspace. Only the
 * event list is read — never the address or its signing secret.
 */
export async function listWebhookEventLists(workspaceId: string): Promise<Result<string[][]>> {
  try {
    const db = getTypedSupabaseClient();
    const { data, error } = await db
      .from('workspace_item_sources')
      .select('events:config->events')
      .eq('workspace_id', workspaceId)
      .eq('type', 'webhook');
    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((r) => {
        const ev = (r as { events?: unknown }).events;
        return Array.isArray(ev) ? ev.map(String) : [];
      }),
      error: null,
    };
  } catch (err) {
    return { data: [], error: message(err) };
  }
}
